// Sport Configuration System

export const STAT_COLORS = {
  primary: '#00E5E5',
  secondary: '#FF9500',
  yellow: '#FFDD00',
  cyan: '#5FFBFB',
  purple: '#BB86FC',
  green: '#4ADE80',
  blue: '#60A5FA',
  red: '#FF6B6B',
  pink: '#FF69B4',
  gold: '#FFD700',
};

export const SPORTS_CONFIG = {
  basketball: {
    id: 'basketball',
    name: 'Basketball',
    emoji: '🏀',
    stats: [
      { key: 'ft_made', label: 'Free Throw', shortLabel: 'FT', color: STAT_COLORS.primary, pointValue: 1 },
      { key: 'two_pt_made', label: '2 Point', shortLabel: '2PT', color: STAT_COLORS.secondary, pointValue: 2 },
      { key: 'three_pt_made', label: '3 Point', shortLabel: '3PT', color: STAT_COLORS.yellow, pointValue: 3 },
      { key: 'rebounds', label: 'Rebound', shortLabel: 'REB', color: STAT_COLORS.cyan },
      { key: 'assists', label: 'Assist', shortLabel: 'AST', color: STAT_COLORS.purple },
      { key: 'steals', label: 'Steal', shortLabel: 'STL', color: STAT_COLORS.green },
      { key: 'blocks', label: 'Block', shortLabel: 'BLK', color: STAT_COLORS.blue },
      { key: 'turnovers', label: 'Turnover', shortLabel: 'TO', color: STAT_COLORS.red },
      { key: 'fouls', label: 'Foul', shortLabel: 'FOUL', color: STAT_COLORS.pink },
    ],
    calculatePoints: (stats) => (stats.ft_made || 0) + (stats.two_pt_made || 0) * 2 + (stats.three_pt_made || 0) * 3,
    primaryStatLabel: 'PTS',
  },
  soccer: {
    id: 'soccer',
    name: 'Soccer',
    emoji: '⚽',
    stats: [
      { key: 'goals', label: 'Goal', shortLabel: 'GOAL', color: STAT_COLORS.primary, pointValue: 1 },
      { key: 'assists', label: 'Assist', shortLabel: 'AST', color: STAT_COLORS.purple },
      { key: 'shots', label: 'Shot', shortLabel: 'SHOT', color: STAT_COLORS.secondary },
      { key: 'saves', label: 'Save (GK)', shortLabel: 'SAVE', color: STAT_COLORS.green },
      { key: 'steals', label: 'Steal', shortLabel: 'STL', color: STAT_COLORS.cyan },
    ],
    calculatePoints: (stats) => stats.goals || 0,
    primaryStatLabel: 'GOALS',
  },
  volleyball: {
    id: 'volleyball',
    name: 'Volleyball',
    emoji: '🏐',
    stats: [
      { key: 'kills', label: 'Kill', shortLabel: 'KILL', color: STAT_COLORS.primary },
      { key: 'assists', label: 'Assist', shortLabel: 'AST', color: STAT_COLORS.purple },
      { key: 'digs', label: 'Dig', shortLabel: 'DIG', color: STAT_COLORS.cyan },
      { key: 'blocks', label: 'Block', shortLabel: 'BLK', color: STAT_COLORS.blue },
      { key: 'aces', label: 'Ace', shortLabel: 'ACE', color: STAT_COLORS.green },
    ],
    calculatePoints: (stats) => stats.kills || 0,
    primaryStatLabel: 'KILLS',
  },
  football: {
    id: 'football',
    name: 'Football',
    emoji: '🏈',
    stats: [
      { key: 'touchdowns', label: 'Touchdown', shortLabel: 'TD', color: STAT_COLORS.primary, pointValue: 6 },
      { key: 'pat', label: 'Extra Point', shortLabel: 'PAT', color: STAT_COLORS.secondary, pointValue: 1 },
      { key: 'two_pt', label: '2PT Conv', shortLabel: '2PT', color: STAT_COLORS.yellow, pointValue: 2 },
      { key: 'field_goals', label: 'Field Goal', shortLabel: 'FG', color: STAT_COLORS.gold, pointValue: 3 },
      { key: 'tackles', label: 'Tackle', shortLabel: 'TKL', color: STAT_COLORS.cyan },
      { key: 'sacks', label: 'Sack', shortLabel: 'SACK', color: STAT_COLORS.red },
      { key: 'interceptions', label: 'Interception', shortLabel: 'INT', color: STAT_COLORS.green },
      { key: 'receptions', label: 'Reception', shortLabel: 'REC', color: STAT_COLORS.purple },
      { key: 'fumbles', label: 'Fumble', shortLabel: 'FUM', color: STAT_COLORS.pink },
    ],
    calculatePoints: (stats) => (stats.touchdowns || 0) * 6 + (stats.pat || 0) + (stats.two_pt || 0) * 2 + (stats.field_goals || 0) * 3,
    primaryStatLabel: 'PTS',
  },
  baseball: {
    id: 'baseball',
    name: 'Baseball',
    emoji: '⚾',
    stats: [
      { key: 'hits', label: 'Hit', shortLabel: 'HIT', color: STAT_COLORS.primary, category: 'batting' },
      { key: 'runs', label: 'Run', shortLabel: 'RUN', color: STAT_COLORS.secondary, category: 'batting' },
      { key: 'rbi', label: 'RBI', shortLabel: 'RBI', color: STAT_COLORS.yellow, category: 'batting' },
      { key: 'walks', label: 'Walk', shortLabel: 'BB', color: STAT_COLORS.cyan, category: 'batting' },
      { key: 'stolen_bases', label: 'Stolen Base', shortLabel: 'SB', color: STAT_COLORS.green, category: 'batting' },
      { key: 'home_runs', label: 'Home Run', shortLabel: 'HR', color: STAT_COLORS.gold, category: 'batting' },
      { key: 'errors', label: 'Error', shortLabel: 'ERR', color: STAT_COLORS.pink, category: 'batting' },
      { key: 'pitch_count', label: 'Pitch Count', shortLabel: 'PC', color: STAT_COLORS.primary, category: 'pitching' },
      { key: 'strikes', label: 'Strike', shortLabel: 'STR', color: STAT_COLORS.green, category: 'pitching' },
      { key: 'balls', label: 'Ball', shortLabel: 'BALL', color: STAT_COLORS.red, category: 'pitching' },
      { key: 'strikeouts_pitch', label: 'Strikeout', shortLabel: 'K', color: STAT_COLORS.cyan, category: 'pitching' },
      { key: 'walks_allowed', label: 'Walk Allowed', shortLabel: 'BB', color: STAT_COLORS.yellow, category: 'pitching' },
      { key: 'hits_allowed', label: 'Hit Allowed', shortLabel: 'H', color: STAT_COLORS.purple, category: 'pitching' },
      { key: 'runs_allowed', label: 'Run Allowed', shortLabel: 'R', color: STAT_COLORS.pink, category: 'pitching' },
      { key: 'innings', label: 'Inning', shortLabel: 'IP', color: STAT_COLORS.gold, category: 'pitching' },
    ],
    calculatePoints: (stats) => stats.runs || 0,
    primaryStatLabel: 'RUNS',
    hasBattingPitching: true,
  },
  softball: {
    id: 'softball',
    name: 'Softball',
    emoji: '🥎',
    stats: [
      { key: 'hits', label: 'Hit', shortLabel: 'HIT', color: STAT_COLORS.primary, category: 'batting' },
      { key: 'runs', label: 'Run', shortLabel: 'RUN', color: STAT_COLORS.secondary, category: 'batting' },
      { key: 'rbi', label: 'RBI', shortLabel: 'RBI', color: STAT_COLORS.yellow, category: 'batting' },
      { key: 'walks', label: 'Walk', shortLabel: 'BB', color: STAT_COLORS.cyan, category: 'batting' },
      { key: 'stolen_bases', label: 'Stolen Base', shortLabel: 'SB', color: STAT_COLORS.green, category: 'batting' },
      { key: 'home_runs', label: 'Home Run', shortLabel: 'HR', color: STAT_COLORS.gold, category: 'batting' },
      { key: 'errors', label: 'Error', shortLabel: 'ERR', color: STAT_COLORS.pink, category: 'batting' },
      { key: 'pitch_count', label: 'Pitch Count', shortLabel: 'PC', color: STAT_COLORS.primary, category: 'pitching' },
      { key: 'strikes', label: 'Strike', shortLabel: 'STR', color: STAT_COLORS.green, category: 'pitching' },
      { key: 'balls', label: 'Ball', shortLabel: 'BALL', color: STAT_COLORS.red, category: 'pitching' },
      { key: 'strikeouts_pitch', label: 'Strikeout', shortLabel: 'K', color: STAT_COLORS.cyan, category: 'pitching' },
      { key: 'walks_allowed', label: 'Walk Allowed', shortLabel: 'BB', color: STAT_COLORS.yellow, category: 'pitching' },
      { key: 'hits_allowed', label: 'Hit Allowed', shortLabel: 'H', color: STAT_COLORS.purple, category: 'pitching' },
      { key: 'runs_allowed', label: 'Run Allowed', shortLabel: 'R', color: STAT_COLORS.pink, category: 'pitching' },
      { key: 'innings', label: 'Inning', shortLabel: 'IP', color: STAT_COLORS.gold, category: 'pitching' },
    ],
    calculatePoints: (stats) => stats.runs || 0,
    primaryStatLabel: 'RUNS',
    hasBattingPitching: true,
  },
};

export const ENABLED_SPORTS = ['basketball', 'soccer', 'volleyball', 'football', 'baseball', 'softball'];

export const getSportConfig = (sport) => SPORTS_CONFIG[sport];

export const getSportStats = (sport) => SPORTS_CONFIG[sport]?.stats || [];

export const calculateSportPoints = (sport, stats) => {
  const config = SPORTS_CONFIG[sport];
  return config?.calculatePoints ? config.calculatePoints(stats) : 0;
};

export const getInitialStats = (sport) => {
  const stats = {};
  const config = SPORTS_CONFIG[sport];
  if (config) {
    config.stats.forEach(stat => {
      stats[stat.key] = 0;
    });
  }
  return stats;
};

export const getPrimaryStatLabel = (sport) => SPORTS_CONFIG[sport]?.primaryStatLabel || 'PTS';

export const hasBattingPitching = (sport) => SPORTS_CONFIG[sport]?.hasBattingPitching || false;

export const getStatsByCategory = (sport, category) => {
  const config = SPORTS_CONFIG[sport];
  if (!config) return [];
  return config.stats.filter(stat => stat.category === category);
};
